/**
 * Workspace chat.
 *
 * Two decisions shape this file.
 *
 * **It polls, and that is on purpose.** A Durable Object would give real
 * push, and it would also add a binding, a migration and a second thing to
 * operate. Polling a cursor is one indexed query per tick, the messages are
 * already encrypted so the transport is uninteresting, and switching to a
 * socket later changes this component and nothing else — no schema change, no
 * API change. The cursor is a message *id*, not a timestamp: ids sort by
 * creation, and a timestamp cursor silently drops anything that landed in the
 * same second as the last one seen.
 *
 * **Nothing here decrypts anything.** `workspaces.ts` owns the key and hands
 * back readable text; this renders it. Same split as `nodes.ts` and the file
 * browser, and it keeps the one file that touches keys small enough to read.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { errorText, api, type WorkspaceMemberWire } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { Avatar, Panel } from './Shell';
import { fetchMessages, sendMessage, type DecryptedMessage, type Channel } from '../workspaces';
import { formatWhen, monoPlain } from '../theme';
import { SkeletonMessages } from './Skeleton';
import { parseMessageBody } from '../crypto/workspace';
import { useT } from '../i18n';

/** How often to ask for new messages while the tab is visible. Slow enough to
 *  be unremarkable on a laptop battery, fast enough to feel like chat. */
const POLL_MS = 5000;
/** Hidden tabs back right off — a workspace left open in a background tab
 *  should not be the reason a browser never idles. */
const HIDDEN_POLL_MS = 60_000;

export function Chat({
  workspaceId,
  channel,
  members,
  canPost
}: {
  workspaceId: string;
  /** Which conversation this is: the workspace at large, or one project.
   *  Built with `workspaceChannel()` / `projectChannel()` — the key and the
   *  AAD differ per channel, so a message cannot be moved between them. */
  channel: Channel;
  members: WorkspaceMemberWire[];
  canPost: boolean;
}) {
  const t = useT();
  const { Button, Icon } = ds();
  const { me } = useApp();

  const [messages, setMessages] = useState<DecryptedMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // The last id seen, held in a ref so the poll closure never captures a stale
  // one — the interval is set up once and would otherwise poll from the cursor
  // as it stood when the component mounted, re-fetching the same page forever.
  const cursor = useRef<string | undefined>(undefined);
  const scroller = useRef<HTMLDivElement | null>(null);
  const atBottom = useRef(true);

  const absorb = useCallback((incoming: DecryptedMessage[]) => {
    if (!incoming.length) return;
    cursor.current = incoming[incoming.length - 1]!.id;
    setMessages((prev) => {
      // De-duplicate: an optimistic send and the poll can both deliver the same
      // message, and React keys must stay unique.
      const seen = new Set(prev.map((m) => m.id));
      return [...prev, ...incoming.filter((m) => !seen.has(m.id))];
    });
  }, []);

  // Cold load, then mark read. Re-runs when the workspace changes.
  useEffect(() => {
    let cancelled = false;
    cursor.current = undefined;
    setMessages([]);
    setLoading(true);

    void (async () => {
      try {
        const page = await fetchMessages(workspaceId, channel);
        if (cancelled) return;
        absorb(page);
        setError(null);
        await api.workspaces.markRead(workspaceId, channel.projectId).catch(() => {});
      } catch (err) {
        if (!cancelled) {
          setError(errorText(err, 'Could not load messages.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, channel, absorb]);

  // The poll.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const page = await fetchMessages(workspaceId, channel, cursor.current);
        if (cancelled) return;
        if (page.length) {
          absorb(page);
          // Only claim to have read them if they are actually on screen.
          if (document.visibilityState === 'visible') {
            await api.workspaces.markRead(workspaceId, channel.projectId).catch(() => {});
          }
        }
      } catch {
        // A dropped tick is not worth surfacing — the next one recovers, and an
        // error banner that flickers on every flaky request is worse than none.
      } finally {
        if (!cancelled) {
          timer = setTimeout(
            tick,
            document.visibilityState === 'visible' ? POLL_MS : HIDDEN_POLL_MS
          );
        }
      }
    };

    timer = setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [workspaceId, channel, absorb]);

  // Follow the conversation, but only if the reader is already at the bottom —
  // yanking someone out of scrollback to show a new message is worse than
  // making them scroll down.
  useEffect(() => {
    const el = scroller.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;

    setSending(true);
    setError(null);
    try {
      await sendMessage(workspaceId, channel, text, members);
      setDraft('');
      atBottom.current = true;
      // Pull it straight back rather than inventing a local row: the server
      // assigns the id the cursor advances on, so a fabricated one would either
      // duplicate or wedge the poll.
      absorb(await fetchMessages(workspaceId, channel, cursor.current));
    } catch (err) {
      setError(errorText(err, 'Could not send that.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Panel style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 380 }}>
      <div
        style={{
          padding: '14px 20px 12px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}
      >
        <Icon name="message-circle" size={17} />
        <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>{t('chat.chat')}</span>
      </div>

      <div
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget;
          atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
        style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', display: 'grid', gap: 14, alignContent: 'start' }}
      >
        {loading ? (
          <SkeletonMessages />
        ) : messages.length === 0 ? (
          <span style={monoPlain}>{t('chat.no-messages-yet-say-something')}</span>
        ) : (
          messages.map((m) => (
            <Message
              key={m.id}
              message={m}
              mine={m.authorId === me?.user.id}
              members={members}
              meId={me?.user.id}
            />
          ))
        )}
      </div>

      {error && (
        <div style={{ padding: '8px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13, color: 'var(--danger-600)' }}>{error}</span>
        </div>
      )}

      {canPost ? (
        <form
          onSubmit={send}
          style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end'
          }}
        >
          <Composer
            value={draft}
            onChange={setDraft}
            members={members}
            onSubmit={() => void send(new Event('submit') as unknown as React.FormEvent)}
          />
          <Button variant="primary" size="md" type="submit" disabled={sending || !draft.trim()}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </form>
      ) : (
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-subtle)' }}>
          <span style={monoPlain}>{t('chat.you-have-readonly-access-to')}</span>
        </div>
      )}
    </Panel>
  );
}

function Message({
  message,
  mine,
  members,
  meId
}: {
  message: DecryptedMessage;
  mine: boolean;
  members: WorkspaceMemberWire[];
  meId?: string;
}) {
  const t = useT();
  // An undecryptable body is not a message — running it through the mention
  // parser would try to find chips in a placeholder sentence.
  const parts = message.undecryptable
    ? [{ kind: 'text' as const, text: message.body }]
    : parseMessageBody(message.body, members);

  return (
    <div style={{ display: 'grid', gap: 3 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: mine ? 'var(--accent-600)' : 'var(--text-strong)'
          }}
        >
          {mine ? 'You' : message.authorName}
        </span>
        <span style={monoPlain}>{formatWhen(message.createdAt)}</span>
        {message.editedAt && <span style={monoPlain}>{t('chat.edited')}</span>}
      </div>
      <span
        style={{
          fontSize: 14,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: message.undecryptable ? 'var(--text-muted)' : 'var(--text-body)',
          fontStyle: message.undecryptable ? 'italic' : undefined
        }}
      >
        {parts.map((part, i) =>
          part.kind === 'text' ? (
            <React.Fragment key={i}>{part.text}</React.Fragment>
          ) : (
            <MentionChip
              key={i}
              userId={part.userId}
              members={members}
              isMe={part.userId === meId}
            />
          )
        )}
      </span>
    </div>
  );
}

/**
 * A tagged person, drawn as a chip rather than as the `@name` the author typed.
 *
 * Tinted more strongly when it is *you* who was tagged: in a busy channel the
 * question being asked of the message list is "is any of this for me", and
 * answering it should not require reading.
 *
 * A mention of someone since removed from the workspace still has an id but no
 * member row. It renders as a neutral chip rather than disappearing — the
 * message said what it said, and silently dropping a name changes its meaning.
 */
function MentionChip({
  userId,
  members,
  isMe
}: {
  userId: string;
  members: WorkspaceMemberWire[];
  isMe: boolean;
}) {
  const member = members.find((m) => m.userId === userId);
  const name = member?.name || member?.email || 'someone';

  return (
    <span
      title={member?.email}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        // Sits on the text baseline of a pre-wrap span, so vertical-align does
        // the work that flex alignment cannot reach from out here.
        verticalAlign: 'baseline',
        padding: '1px 7px 1px 2px',
        margin: '0 1px',
        borderRadius: 999,
        background: isMe ? 'var(--accent-600)' : 'var(--accent-100)',
        color: isMe ? '#fff' : 'var(--accent-600)',
        fontWeight: 600,
        fontSize: 13.5,
        whiteSpace: 'nowrap'
      }}
    >
      <Avatar name={name} email={member?.email ?? ''} size={17} />
      {name}
    </span>
  );
}

/**
 * The composer, with `@` autocomplete over the member list.
 *
 * The trigger is a bare `@` at a word boundary followed by what has been typed
 * so far. Matching happens entirely here, against members the client already
 * holds — the server is never asked to interpret the text, which is the same
 * reason mention *resolution* lives in `crypto/workspace.ts`.
 */
function Composer({
  value,
  onChange,
  members,
  onSubmit
}: {
  value: string;
  onChange: (next: string) => void;
  members: WorkspaceMemberWire[];
  onSubmit: () => void;
}) {
  const t = useT();
  const input = useRef<HTMLTextAreaElement | null>(null);
  const [highlight, setHighlight] = useState(0);

  /** The `@…` being typed at the caret, or null. */
  const query = useMemo(() => {
    const match = /(^|\s)@([^\s@]*)$/.exec(value);
    return match ? match[2]! : null;
  }, [value]);

  const matches = useMemo(() => {
    if (query === null) return [];
    const needle = query.toLowerCase();
    return members
      // Someone invited but without a vault has no id to tag — mentioning them
      // would store an id the server cannot resolve.
      .filter((m) => m.userId && m.state === 'active')
      .filter((m) => !needle || m.name.toLowerCase().startsWith(needle) || m.email.toLowerCase().startsWith(needle))
      .slice(0, 6);
  }, [query, members]);

  useEffect(() => setHighlight(0), [query]);

  const accept = (member: WorkspaceMemberWire) => {
    // Replace the partial token, keeping whatever came before it.
    onChange(value.replace(/(^|\s)@([^\s@]*)$/, (_, lead: string) => `${lead}@${member.name} `));
    input.current?.focus();
  };

  const open = query !== null && matches.length > 0;

  return (
    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'var(--surface-raised, var(--surface-card))',
            border: '1px solid var(--border-subtle)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(40,28,45,.16)',
            overflow: 'hidden',
            zIndex: 20
          }}
        >
          {matches.map((m, i) => (
            <button
              key={m.id}
              type="button"
              // Mouse-down rather than click: click fires after blur, by which
              // point the caret has moved and the token no longer matches.
              onMouseDown={(e) => {
                e.preventDefault();
                accept(m);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                border: 'none',
                cursor: 'pointer',
                background: i === highlight ? 'var(--accent-100)' : 'transparent',
                fontSize: 13.5,
                color: 'var(--text-strong)'
              }}
            >
              {m.name}
              <span style={{ ...monoPlain, marginLeft: 8 }}>{m.email}</span>
            </button>
          ))}
        </div>
      )}

      <textarea
        ref={input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (open) {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => (h + 1) % matches.length);
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => (h - 1 + matches.length) % matches.length);
              return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              accept(matches[highlight]!);
              return;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              // Nothing to close explicitly — dropping the trailing token is
              // what closes it, and that is the user's to do. Just stop the key
              // reaching a parent dialog.
              return;
            }
          }
          // Enter sends, shift+Enter breaks a line — the convention every chat
          // uses, and the one people type without thinking.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        rows={1}
        placeholder={t('chat.write-a-message-to-mention')}
        style={{
          width: '100%',
          resize: 'none',
          minHeight: 38,
          maxHeight: 140,
          padding: '9px 12px',
          borderRadius: 10,
          border: '1px solid var(--border-strong, var(--border-subtle))',
          background: 'var(--surface-sunken, transparent)',
          color: 'var(--text-body)',
          font: 'inherit',
          fontSize: 14,
          lineHeight: 1.45
        }}
      />
    </div>
  );
}
